import { Button, Card, CardBody, CardFooter, CardTitle, Content } from '@patternfly/react-core';
import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { CreateOrganizationForm } from './CreateOrganizationForm';
import { JoinOffer, JoinOfferData } from './JoinOffer';

export interface ClientlessOnboardingProps {
  refreshUser: () => Promise<void>;
}

type Step = 'form' | 'offer' | 'domain-blocked';

/**
 * Clientless-branch container. Owns the local UI step state (D7): `onboarding_state` alone can't
 * distinguish the create form from the join offer from the domain-blocked terminal, so this holds a
 * local `step`. The authoritative `onboarding_state` only flips via refreshUser() after a real
 * backend transition. A blocked create flips to the offer; declining a VAT offer returns to a fresh
 * form (VAT cleared), while declining a domain offer lands on the sign-out terminal.
 */
export function ClientlessOnboarding({ refreshUser }: ClientlessOnboardingProps) {
  const auth = useAuth();
  const [step, setStep] = useState<Step>('form');
  const [offer, setOffer] = useState<JoinOfferData | null>(null);

  const handleBlocked = (nextOffer: JoinOfferData) => {
    setOffer(nextOffer);
    setStep('offer');
  };

  const handleDecline = () => {
    if (offer?.reason === 'vat') {
      // VAT decline → back to a fresh create form; remounting clears the VAT field (D7/D5).
      setOffer(null);
      setStep('form');
    } else {
      // Domain decline → the terminal; keep `offer` so the screen can name the organization.
      setStep('domain-blocked');
    }
  };

  if (step === 'offer' && offer) {
    return <JoinOffer offer={offer} refreshUser={refreshUser} onDecline={handleDecline} />;
  }

  if (step === 'domain-blocked' && offer) {
    return (
      <Card style={{ maxWidth: '600px' }}>
        <CardTitle>Different account needed</CardTitle>
        <CardBody>
          <Content component="p">
            Your email address belongs to <strong>{offer.organization.name}</strong>, a verified organization that you
            can&apos;t register yourself. To continue, sign in with a different account, or go back to join it.
          </Content>
        </CardBody>
        <CardFooter>
          <Button variant="primary" onClick={() => void auth.signoutRedirect()}>
            Sign in with a different account
          </Button>
          <Button variant="link" onClick={() => setStep('offer')}>
            Go back
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return <CreateOrganizationForm refreshUser={refreshUser} onBlocked={handleBlocked} />;
}
